import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ForeignNationalInfoInput } from '@access-cms/api-client';

import { Accordion, type AccordionItem } from '../../../components/Accordion';
import { SectionId } from '../constants';
import { type ApprovalDetailsSection, isAdditionalAccessSection, type PivCardSelection } from '../types';
import ApprovalDetailsAdditionalAccessContent from './ApprovalDetailsAdditionalAccessContent';
import ApprovalDetailsForeignNationalContent from './ApprovalDetailsForeignNationalContent';
import ApprovalDetailsSectionContent from './ApprovalDetailsSectionContent';
import type { ApprovalEditState } from './ApprovalDetailsEditableRow';
import ApprovalDetailsSectionIcon from './ApprovalDetailsSectionIcon';

/** Sections rendered as validated forms rather than read-only rows. */
const FORM_SECTION_IDS: string[] = [SectionId.ADDITIONAL_ACCESS_INFORMATION, SectionId.FOREIGN_NATIONAL_INFORMATION];

interface ApprovalDetailsAccordionProps {
  applicationId?: string;
  editStateByRowId: Record<string, ApprovalEditState>;
  formResetKey?: number;
  onCancel: (rowId: string) => void;
  onEdit: (rowId: string) => void;
  /** FN approver view renders the Foreign National Information section read-only. */
  foreignNationalReadOnly?: boolean;
  onFormPendingChangesChange?: (hasPendingChanges: boolean) => void;
  /** Lifts the Foreign National Information answers up for persistence on approve. */
  onForeignNationalValuesChange?: (values: ForeignNationalInfoInput) => void;
  onReasonChange: (rowId: string, value: string) => void;
  onRemove: (rowId: string) => void;
  onRequestEdit: (rowId: string) => void;
  onPivCardSelectionChange?: (value: PivCardSelection) => void;
  onReviewedSectionsChange?: (reviewedSectionIds: string[]) => void;
  onSave: (rowId: string) => void;
  onWorkingSixMonthsChange?: (value: boolean) => void;
  sections: ApprovalDetailsSection[];
}

function ApprovalDetailsAccordion({
  applicationId,
  editStateByRowId,
  foreignNationalReadOnly = false,
  formResetKey = 0,
  onCancel,
  onEdit,
  onFormPendingChangesChange,
  onForeignNationalValuesChange,
  onReasonChange,
  onRemove,
  onRequestEdit,
  onPivCardSelectionChange,
  onReviewedSectionsChange,
  onSave,
  onWorkingSixMonthsChange,
  sections,
}: ApprovalDetailsAccordionProps) {
  const defaultOpenItems: string[] = [];
  const [viewedSectionIds, setViewedSectionIds] = useState<Set<string>>(() => new Set(defaultOpenItems));
  const [formComplete, setFormComplete] = useState<Record<string, boolean>>({});
  const [showValidation, setShowValidation] = useState<Record<string, boolean>>({});
  const [formPending, setFormPending] = useState<Record<string, boolean>>({});
  const [pendingScrollSection, setPendingScrollSection] = useState<string | null>(null);
  const openedFormSectionsRef = useRef<Set<string>>(
    new Set(defaultOpenItems.filter((id) => FORM_SECTION_IDS.includes(id))),
  );

  const returnedSectionIds = sections
    .filter((section) => {
      const itemIds = section.documentGroups
        ? section.documentGroups.flatMap((group) => group.documents.map((file) => file.id))
        : section.rows.map((row) => row.id);

      return itemIds.some((itemId) => {
        const state = editStateByRowId[itemId];

        return state?.mode === 'editing' || state?.mode === 'saved';
      });
    })
    .map((section) => section.id);

  const [pendingViewedSection, setPendingViewedSection] = useState<string | null>(null);

  // Deliberately leaves formComplete alone: the form sections remount on formResetKey (it's in their
  // key) and re-report completion from their mount effects, which run before this parent effect —
  // clearing here would wipe what they just reported.
  useEffect(() => {
    setShowValidation({});
    setFormPending({});
    openedFormSectionsRef.current.clear();
    setViewedSectionIds((prev) => {
      if (!FORM_SECTION_IDS.some((id) => prev.has(id))) {
        return prev;
      }

      const next = new Set(prev);
      FORM_SECTION_IDS.forEach((id) => next.delete(id));

      return next;
    });
  }, [formResetKey]);

  useEffect(() => {
    onFormPendingChangesChange?.(Object.values(formPending).some(Boolean));
  }, [formPending, onFormPendingChangesChange]);

  const markViewed = useCallback((sectionId: string) => {
    setViewedSectionIds((prev) => {
      if (prev.has(sectionId)) {
        return prev;
      }

      const next = new Set(prev);
      next.add(sectionId);

      return next;
    });
  }, []);

  const unmarkViewed = useCallback((sectionId: string) => {
    setViewedSectionIds((prev) => {
      if (!prev.has(sectionId)) {
        return prev;
      }

      const next = new Set(prev);
      next.delete(sectionId);

      return next;
    });
  }, []);

  // Stable per-section callbacks so the memoized form contents don't re-render on every accordion render.
  const formHandlers = useMemo(
    () =>
      Object.fromEntries(
        FORM_SECTION_IDS.map((sectionId) => [
          sectionId,
          {
            onCompletionChange: (isComplete: boolean) => {
              setFormComplete((prev) => (prev[sectionId] === isComplete ? prev : { ...prev, [sectionId]: isComplete }));

              if (isComplete && openedFormSectionsRef.current.has(sectionId)) {
                markViewed(sectionId);
              } else if (!isComplete) {
                unmarkViewed(sectionId);
              }
            },
            onPendingChangesChange: (hasPendingChanges: boolean) => {
              setFormPending((prev) =>
                prev[sectionId] === hasPendingChanges ? prev : { ...prev, [sectionId]: hasPendingChanges },
              );
            },
          },
        ]),
      ),
    [markViewed, unmarkViewed],
  );

  const handleToggle = useCallback(
    (itemId: string, isOpen: boolean) => {
      if (isOpen) {
        setPendingScrollSection(itemId);

        if (FORM_SECTION_IDS.includes(itemId)) {
          openedFormSectionsRef.current.add(itemId);

          if (formComplete[itemId]) {
            markViewed(itemId);
          }
        } else {
          setPendingViewedSection(itemId);
        }
      } else if (FORM_SECTION_IDS.includes(itemId)) {
        setShowValidation((prev) => (prev[itemId] ? prev : { ...prev, [itemId]: true }));

        if (formComplete[itemId]) {
          markViewed(itemId);
        }
      }
    },
    [formComplete, markViewed],
  );

  useEffect(() => {
    if (pendingViewedSection) {
      setViewedSectionIds((prev) => {
        if (prev.has(pendingViewedSection)) {
          setPendingViewedSection(null);

          return prev;
        }

        const next = new Set(prev);
        next.add(pendingViewedSection);
        setPendingViewedSection(null);

        return next;
      });
    }
  }, [pendingViewedSection]);

  useEffect(() => {
    if (!pendingScrollSection) {
      return;
    }

    const accordionItem = document.querySelector<HTMLElement>(`[data-testid="accordion-item-${pendingScrollSection}"]`);

    if (accordionItem && typeof accordionItem.scrollIntoView === 'function') {
      accordionItem.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    setPendingScrollSection(null);
  }, [pendingScrollSection]);

  const hasFormError = useCallback(
    (sectionId: string) => Boolean(showValidation[sectionId]) && !formComplete[sectionId],
    [formComplete, showValidation],
  );

  useEffect(() => {
    const reviewedSections = sections
      .filter((section) => viewedSectionIds.has(section.id) && !hasFormError(section.id))
      .map((section) => section.id);

    onReviewedSectionsChange?.(reviewedSections);
  }, [hasFormError, onReviewedSectionsChange, returnedSectionIds, sections, viewedSectionIds]);

  const items: AccordionItem[] = sections.map((section) => {
    let iconVariant: 'default' | 'returned' | 'success' | 'error' = 'default';

    if (hasFormError(section.id)) {
      iconVariant = 'error';
    } else if (returnedSectionIds.includes(section.id)) {
      iconVariant = 'returned';
    } else if (viewedSectionIds.has(section.id)) {
      iconVariant = 'success';
    }

    const content = isAdditionalAccessSection(section) ? (
      <ApprovalDetailsAdditionalAccessContent
        key={`${section.id}-content-${formResetKey}`}
        initialValue={section.additionalAccessData}
        showValidation={showValidation[section.id] ?? false}
        onPivCardSelectionChange={onPivCardSelectionChange}
        onWorkingSixMonthsChange={onWorkingSixMonthsChange}
        {...formHandlers[section.id]}
      />
    ) : section.id === SectionId.FOREIGN_NATIONAL_INFORMATION ? (
      <ApprovalDetailsForeignNationalContent
        key={`${section.id}-content-${formResetKey}`}
        applicationId={applicationId}
        readOnly={foreignNationalReadOnly}
        showValidation={showValidation[section.id] ?? false}
        onValuesChange={onForeignNationalValuesChange}
        {...formHandlers[section.id]}
      />
    ) : (
      <ApprovalDetailsSectionContent
        key={`section-content-${section.id}`}
        applicationId={applicationId}
        editStateByRowId={editStateByRowId}
        section={section}
        onCancel={onCancel}
        onEdit={onEdit}
        onReasonChange={onReasonChange}
        onRemove={onRemove}
        onRequestEdit={onRequestEdit}
        onSave={onSave}
      />
    );

    return {
      content,
      icon: <ApprovalDetailsSectionIcon variant={iconVariant} />,
      id: section.id,
      required: true,
      title: section.title,
    };
  });

  return (
    <Accordion
      className='approval-details-accordion'
      data-testid='approval-details-accordion'
      defaultOpenItems={defaultOpenItems}
      items={items}
      onToggle={handleToggle}
    />
  );
}

export default ApprovalDetailsAccordion;
